import { Colors, DraggableList, Icon, Text, type DraggableItem } from '@amelio/ui-library';

import { AgendaEditorProvider } from './AgendaEditorContext';
import {
    AgendaGeneratingState,
    type AgendaGeneratingNotice,
} from './components/AgendaGeneratingState';
import { AgendaHeader } from './components/AgendaHeader';
import { AgendaItemRow } from './components/AgendaItemRow';
import { AgendaLanguageLabel } from './components/AgendaLanguageLabel';
import { AgendaOpeningStatement } from './components/AgendaOpeningStatement';
import { AgendaOpeningStatementInline } from './components/AgendaOpeningStatementInline';
import { EditDiscussionPointModal } from './components/EditDiscussionPointModal';
import { TopicEditForm } from './components/TopicEditForm';

import type { AgendaItemView } from './agendaViewModel';
import type { AgendaEditorController } from './useAgendaEditor';
import type { ContentLanguage } from '@/utils/contentLanguage';
import type { ReactNode } from 'react';

import { useAmelioTranslations } from '@/hooks';

interface AgendaEditorProps {
    /** The editing state machine, from `useAgendaEditor`. */
    editor: AgendaEditorController;
    totalMeetingTimeMinutes: number;
    disabled?: boolean;
    itemListDataTestId?: string;
    /** Replaces `AgendaHeader`'s default (cycle-review) title and description. */
    heading?: ReactNode;
    /**
     * The reader's language, for an agenda read by others than its author: when the agenda is written in
     * the other language, a label under the header says which. Left out, no label is shown.
     */
    readerLanguage?: ContentLanguage;
    /** Rendered between the header and the opening statement — the AI banner of the cycle review. */
    banner?: ReactNode;
    /** Extra controls beside each row, for a flow that owns item actions of its own. */
    renderItemActions?: (item: AgendaItemView) => ReactNode;
    /** The "add a topic" trigger: each flow styles and gates it its own way, so it stays with its host. */
    addTopicControl?: ReactNode;
    /**
     * How the opening statement is edited: `form`, a textarea behind a pencil with a save button,
     * `inline`, its text turning into a field in place, under a larger title and the hint that hands
     * it over to the manager, or `none` for a flow that writes it in a section of its own.
     * @default 'form'
     */
    openingStatementEditing?: 'form' | 'inline' | 'none';
    /** Whether a discussion point offers the employee's response: an agenda written before any
     * meeting, such as a plan's, has nobody to answer yet.
     * @default true */
    withResponses?: boolean;
    /** A read-only agenda reads as a record of the meeting: no disabled controls, and empty zones say
     * what was not prepared or noted. Without it, a read-only agenda shows its controls disabled.
     * @default false */
    readOnlyAsRecord?: boolean;
    /**
     * For a flow where the AI can write a new agenda. A status region is then always there, under the
     * banner, and only its content changes: while `isGenerating`, the notice and a skeleton stand in
     * it where the agenda was, so nothing can be edited meanwhile. Left out, no region is rendered.
     */
    generation?: { isGenerating: boolean; notice: AgendaGeneratingNotice };
}

/**
 * The agenda editor itself: header, opening statement, the draggable list of topics with their
 * discussion points, the inline "new topic" form and the discussion-point modal. Purely
 * presentational — every piece of state and every mutation comes from the `editor` it is given —
 * so the post-review meeting of a cycle and a thematic meeting render one and the same editor.
 */
export const AgendaEditor = ({
    editor,
    totalMeetingTimeMinutes,
    disabled = false,
    itemListDataTestId = 'meeting-agenda-item-list',
    heading,
    readerLanguage,
    banner,
    renderItemActions,
    addTopicControl,
    openingStatementEditing = 'form',
    withResponses = true,
    readOnlyAsRecord = false,
    generation,
}: AgendaEditorProps) => {
    const { t } = useAmelioTranslations();
    const { items, openingStatement, topics, points } = editor;
    const isRecord = disabled && readOnlyAsRecord;

    const draggableItems: DraggableItem<AgendaItemView>[] = items.map((item) => ({
        id: item.id,
        data: item,
    }));

    return (
        <AgendaEditorProvider value={{ editor, disabled, isRecord, withResponses }}>
            <AgendaHeader
                totalMeetingTimeMinutes={totalMeetingTimeMinutes}
                heading={heading}
            />

            {readerLanguage && (
                <AgendaLanguageLabel
                    agendaLanguage={editor.language}
                    readerLanguage={readerLanguage}
                />
            )}

            {banner}

            {generation && (
                <div
                    role={'status'}
                    data-testid={'agenda-generation-status'}
                >
                    {generation.isGenerating && (
                        <div className={'mt-6'}>
                            <AgendaGeneratingState notice={generation.notice} />
                        </div>
                    )}
                </div>
            )}

            {generation?.isGenerating ? null : (
                <>
                    {openingStatementEditing === 'inline' && (
                        <AgendaOpeningStatementInline
                            value={openingStatement.value}
                            disabled={disabled}
                            onSave={openingStatement.save}
                        />
                    )}

                    {openingStatementEditing === 'form' && (
                        <AgendaOpeningStatement
                            value={openingStatement.value}
                            isEditing={openingStatement.isEditing}
                            disabled={disabled}
                            onChange={openingStatement.change}
                            onEdit={openingStatement.startEditing}
                            onBlur={openingStatement.finishEditing}
                        />
                    )}

                    <div className={'border-base-100 mt-4 border-t pt-4'}>
                        {isRecord && items.length === 0 && (
                            <Text
                                size={'small'}
                                color={Colors.BASE_500}
                            >
                                {t('meetingAgenda.readOnly.noTopics')}
                            </Text>
                        )}

                        <DraggableList
                            items={draggableItems}
                            onReorder={(reordered) =>
                                topics.reorder(reordered.map((entry) => entry.data))
                            }
                            gap={12}
                            dataTestId={itemListDataTestId}
                            useDragHandle
                            disabled={disabled}
                            // The row must stay the element `renderItem` returns: `DraggableList` clones
                            // the drag props onto it, so a wrapper would take them instead of the handle.
                            renderItem={({ data: item }) => (
                                <AgendaItemRow
                                    item={item}
                                    actions={renderItemActions?.(item)}
                                />
                            )}
                        />
                    </div>

                    {topics.isAdding && (
                        <div
                            className={
                                'border-base-200 mt-3 flex w-full items-center gap-3 rounded-xl border bg-white px-4 py-3'
                            }
                        >
                            <Icon
                                name={'essential-menu'}
                                size={'sm'}
                                color={Colors.BASE_300}
                                ariaHidden
                            />

                            <TopicEditForm
                                onConfirm={topics.confirmAdd}
                                onCancel={() => topics.setIsAdding(false)}
                                dataTestId={'add-topic-form'}
                            />
                        </div>
                    )}

                    {addTopicControl}

                    <EditDiscussionPointModal
                        isOpen={points.details !== null}
                        point={points.details?.point ?? null}
                        onClose={points.closeDetails}
                        onSave={(title, context) => points.saveDetails({ title, context })}
                        isTitleEditable={false}
                    />
                </>
            )}
        </AgendaEditorProvider>
    );
};
